import { HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { HotelReserveCancellationPoliciesPropertiesTypes } from "@website/accommodation-ui/hotel-reserve-cancellation-policies";
import { CMSSeoContentData } from "@website/website-ui/services/seo-contents/cms-types";
import { ReserveCommentPropertiesType } from "@website/widget/reserve-comment";
import { ReserveRatePropertiesTypes } from "@website/widget/reserve-rate";
import { HotelDomesticType } from "../index.types";

export type HotelReserveRateTypes =
  | "facilities"
  | "food"
  | "cleaning"
  | "friendly"
  | "priceQuality"
  | "hygiene"
  | "location";

export enum PersianDomesticRatings {
  cleaning = "وضعیت نظافت اقامتگاه",
  facilities = "امکانات اقامتگاه",
  food = "کیفیت غذا و پذیرایی",
  friendly = "خوشرویی و رفتار کارکنان اقامتگاه",
  location = "موقعیت مکانی و دسترسی اقامتگاه",
  priceQuality = "کیفیت اقامتگاه نسبت به قیمت آن"
}

export type HotelDomesticRoomType = {
  room_id: number;
  room_type_id: number;
  room_type_title: string;
  hotel_id: number;
  rule_id: number;
  adults: number;
  title: string;
  available_rooms: number;
  extra_bed: number;
  breakfast_included: boolean;
  provider: string;
  booking_type: string;
  book_need_confirm: boolean;
  min_stay: number;
  description: string;
  pe_badge_title?: string;
  promotion: {
    average_discount_percent: number;
  };
  facilities_tags: Array<{
    id: number;
    icon: string;
    title: string;
  }>;
  cover_media: {
    id: number;
    media: string;
  };
  prices: {
    base_price: number;
    local_price: number;
    local_price_off: number;
    extra_bed_price: number;
    original_sell_price: number;
    child_final_price: number;
  };
  badges: HotelDomesticType["badges"];
  rack_included: boolean;
  racks: Array<{
    availability: number;
    from: string;
    price: number;
    price_off: number;
    to: string;
  }>;
  snapp_pro_discount: boolean;
};

export type HotelDomesticRoomsType = Array<HotelDomesticRoomType>;

export type HotelDomesticRateDataType = Record<
  HotelReserveRateTypes,
  string
> & {
  recommendationCount: number;
  recommendationType: HotelReserveRateTypes & "fullRate" & "recommendation";
  rateCount: number;
  overall: string;
  aiSummary: string;
};

export type HotelDomesticReviewDataType = {
  page: number;
  page_size: number;
  result: [
    {
      id: number;
      helpfulSubmitted: boolean;
      comment: string;
      cons: [string];
      createdAt: string;
      avgRating: number;
      dateCount: number;
      passenger: string;
      propertyId: number;
      pros: [string];
      roomId: number;
      roomTitle: string;
      status: string;
      source: string;
      images: Array<string>;
    }
  ];
  total: number;
};

export type HotelDomesticCalendarType = {
  date: string;
  available_rooms: number;
  prices: {
    local_price: number;
    local_price_off: number;
  };
};

export type HotelDomesticHotelDataType = {
  id: number;
  title: string;
  status: false;
  accommodation_type: string;
  reviews: {
    ptp_ratings: string;
    ptp_reviews: string;
  };
  description: string;
  snapp_description?: string;
  cover_media: {
    id: number;
    media: string;
    title: string;
  };
  gallery: Array<{
    id: number;
    media: string;
    title: string;
    media_type: string;
    order_no: number;
  }>;
  seo_meta_desc: string;
  seo_title: string;
  stars: number;
  is_guarantee: false;
  contract_type: string;
  address: string;
  city_id: number;
  city_title: string;
  phones: Array<string>;
  faxes: Array<string>;
  latlon: Array<number>;
  check_in_time: string;
  check_out_time: string;
  facilities: Array<{
    id: number;
    icon: string;
    title: string;
  }>;
  booking_type: string;
  foreigners_fee: false;
  infant_age: number;
  child_age: number;
  hotel_policy: string;
  cancellation_policy: string;
  categories: Array<{
    id: number;
    name: string;
    main_category_type: string;
  }>;
  pe_badge_title: string;
  badges: Array<{
    color: string;
    title: string;
  }>;
};

export type HotelReviewDataType = {
  lastReviewId?: number;
  rateReviews?: Array<ReserveCommentPropertiesType>;
};

export type HotelPagePropertiesType = {
  hotelData?: HotelDomesticHotelDataType;
  cancellationPoliciesData?: HotelReserveCancellationPoliciesPropertiesTypes;
  hotelRateData?: Omit<ReserveRatePropertiesTypes, "hotelType">;
  hotelReviewData?: HotelReviewDataType;
  seoData?: CMSSeoContentData;
  hotelPriceRange?: { min: number; max: number };
  hotelRecommendations?: Array<HotelInfo>;
};

export type HotelCreateBookPropertiesTypes = {
  roomId?: number;
  id?: number;
  ruleId?: number;
  bookNeedConfirm?: boolean;
  bookType: string;
  dateFrom?: string;
  dateTo?: string;
  hotelId: number;
  promotion?: number;
  provider?: string;
  availableRooms: number;
  price?: number;
  priceOff?: number;
  adults?: number;
  title?: string;
  roomCount?: number;
  nightCount?: number;
  roomTypeId?: number;
  basePrice?: number;
  originalSellPrice?: number;
  childPrice?: number;
  extraBed?: number;
  hasBreakfast?: boolean;
};
